import React from 'react';
import { useEffect, useMemo } from 'react';
import { Box, Paper, Typography, CircularProgress, Button } from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import {MessageTxt} from "./MessageText";
import { useInView } from 'react-intersection-observer';
import { BASE_DOMAIN } from '../../api/config';
import { formatChatDate, isSameDay, messageDate } from './chatDate';

interface MessageListProps {
    messages: any[];
    conversation: any;
    currentUser: any;
    imageErrors: Record<string, boolean>;
    setImageErrors: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
    setSelectedImage: (url: string | null) => void;
    messagesEndRef: React.RefObject<HTMLDivElement | null>;
    setNewMessageText: (text: string) => void;
    replyTo: any;
    setReplyTo: (msg: any) => void;
    loadMore?: () => void; 
    hasMore?: boolean;
    loadingMore?: boolean;
    scrollToBottom: () => void; 
    // Переотправка «не отправленного» сообщения (текст или фото).
    onRetry?: (message: any) => void;
}

// Состояние исходящего сообщения: часы — «отправляется», галочки —
// отправлено/доставлено/прочитано. «Не отправлено» рисуется отдельно,
// вместе с кнопкой «Повторить».
const OutgoingStatus: React.FC<{ message: any }> = ({ message }) => {
    // 'pending' — очередь на бэкенде, 'sending' — оптимистичное состояние
    // фронтенда до ответа POST: и то и другое показываем часами.
    if (message.status === 'sending' || message.status === 'pending') {
        return <AccessTimeIcon titleAccess="Отправляется" sx={{ fontSize: 18, opacity: 0.5 }} />;
    }
    if (message.status === 'read' || message.isRead) {
        return <DoneAllIcon sx={{ fontSize: 18, color: '#2196f3' }} />;
    }
    return <DoneIcon sx={{ fontSize: 18, opacity: 0.4 }} />;
};

const getSenderColor = (userId?: string) => {
    if (!userId) return '#1976d2';
    const colors = ['#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#2196f3', '#009688', '#4caf50', '#ff9800', '#ff5722'];
    const index = Math.abs(userId.split('').reduce((a, b) => (a << 5) - a + b.charCodeAt(0), 0)) % colors.length;
    return colors[index];
};

// Разделитель переписки по дням в стиле Telegram: полупрозрачная плашка
// по центру («Сегодня», «Вчера», «3 ноября») поверх фона чата.
const DateSeparator: React.FC<{ label: string }> = ({ label }) => (
    <Box sx={{ display: 'flex', justifyContent: 'center', my: 1.5 }}>
        <Box sx={{
            px: 1.5,
            py: 0.5,
            borderRadius: '12px',
            bgcolor: 'rgba(0, 0, 0, 0.22)',
            color: '#ffffff',
            fontSize: '0.75rem',
            fontWeight: 500,
            lineHeight: 1.6,
            userSelect: 'none',
            pointerEvents: 'none',
        }}>
            {label}
        </Box>
    </Box>
);

export const MessageList: React.FC<MessageListProps> = ({
    messages,
    conversation,
    currentUser,
    imageErrors,
    setImageErrors,
    setSelectedImage,
    messagesEndRef,
    setNewMessageText,
    replyTo,
    setReplyTo,
    loadMore,
    hasMore,
    loadingMore,
    scrollToBottom,
    onRetry
}) => {
    // Проверяем, группа ли это (из payload беседы или флага)
    const isGroup = conversation?.isGroup || conversation?.payload?.isGroup;
    const serverBase = BASE_DOMAIN;
    //для подгрузки старых сообщений
    const { ref, inView } = useInView({
        threshold: 0,
        rootMargin: '100px 0px', // Начинаем грузить чуть раньше, чем юзер доскроллил до верха
    });

    useEffect(() => {
        if (inView && hasMore && !loadingMore && loadMore) {
            loadMore();
        }
    }, [inView, hasMore, loadMore, loadingMore]);

    // Подписи разделителей дней: заполняем только для сообщений, с которых
    // начинается новый день в хронологии (messages идут от старых к новым).
    const dayLabels = useMemo(() => {
        return messages.map((message: any, index: number) => {
            const date = messageDate(message);
            if (!date) return undefined;
            const previous = index > 0 ? messageDate(messages[index - 1]) : null;
            if (previous && isSameDay(previous, date)) return undefined;
            return formatChatDate(date);
        });
    }, [messages]);

    return (
        <Box sx={{
            flexGrow: 1, 
            minHeight: 0, 
            overflowY: 'auto', 
            p: 3, 
            display: 'flex', 
            // Низ чата теперь является точкой старта (нулем) для скролла.
            flexDirection: 'column-reverse', 
            WebkitOverflowScrolling: 'touch', 
            // МАГИЯ ПРОТИВ ЗАМОРОЗКИ:
            contain: 'content',           // Изолирует рендер списка от остального DOM
            willChange: 'scroll-position', // Готовит GPU к прокрутке заранее

            bgcolor: '#5c7bb0',
            // Исправленный SVG фон
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org fill='%23ffffff' fill-opacity='0.15'%3E%3Ccircle cx='40' cy='40' r='2'/%3E%3C/g%3E%3C/svg%3E")`,
        }}>
            {/* 1. Якорь конца сообщений. В режиме reverse он физически находится в начале DOM */}
            <div ref={messagesEndRef} />

            {/* 2. Рендерим сообщения. 
                Используем [...messages].reverse(), чтобы новые сообщения были внизу (у "нуля"),
                а старые уходили вверх.
            */}
            {[...messages].reverse().map((msg: any,index: number) => {

                const isLastMessage = index === messages.length - 1;

                // Индекс сообщения в хронологическом порядке (массив развёрнут).
                const chronoIndex = messages.length - 1 - index;
                // Плашка дня для первого сообщения дня по хронологии. Внутри
                // строки порядок обычный, поэтому плашка встаёт НАД пузырём.
                const dayLabel = dayLabels[chronoIndex];

                const imageSrc = msg.preview ||
                    (msg.payload?.filePath ? `${serverBase}${msg.payload.filePath}` : null) ||
                    (msg.attachments?.[0]?.url ? `${serverBase}${msg.attachments[0].url}` : null);
                
                const isMine = (String(msg.payload?.senderId).toLowerCase() === String(currentUser?.id).toLowerCase()) || 
                             (msg.direction === 'outbound' || msg.direction === 'outgoing');

                const hasError = imageErrors[msg.id] || false;

                // Данные отправителя
                const senderName = msg.payload?.senderName || 'Участник';
                const senderId = msg.payload?.senderId;

                return (
                    <Box key={msg.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: isMine ? 'flex-end' : 'flex-start', mb: 2, transform: 'translateZ(0)' }}>
                        {dayLabel && (
                            <Box sx={{ alignSelf: 'stretch', mb: 1 }}>
                                <DateSeparator label={dayLabel} />
                            </Box>
                        )}
                        <Paper elevation={2} sx={{
                            p: imageSrc ? 1 : 1.5,
                            bgcolor: isMine ? '#d1e4ff' : '#ffffff',
                            maxWidth: '85%', 
                            overflow: 'hidden',
                            borderRadius: isMine ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                            wordBreak: 'break-word'
                        }}>

                            {/* ИМЯ ОТПРАВИТЕЛЯ (Только в группах и не для моих сообщений) */}
                            {isGroup && !isMine && (
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        fontWeight: 'bold', 
                                        display: 'block', 
                                        mb: 0.5, 
                                        color: getSenderColor(senderId),
                                        cursor: 'default',
                                        fontSize: '0.8rem'
                                    }}
                                >
                                    {senderName}
                                </Typography>
                            )}

                            {imageSrc && (
                                <Box
                                    sx={{ position: 'relative', lineHeight: 0, cursor: 'pointer' }}
                                    onClick={() => !msg.isUploading && !hasError && setSelectedImage(imageSrc)}
                                >
                                    {hasError ? (
                                        <Box sx={{
                                            width: '200px', height: '150px',
                                            bgcolor: 'rgba(0,0,0,0.05)', borderRadius: '12px',
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                            border: '1px dashed rgba(0,0,0,0.2)'
                                        }}>
                                            <ImageNotSupportedIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                                            <Typography variant="caption" sx={{ opacity: 0.5 }}>Фото недоступно</Typography>
                                        </Box>
                                    ) : (
                                        <img
                                            src={imageSrc}
                                            loading="lazy"      // Грузим только то, что на экране
                                            decoding="async"    // Декодируем картинку в фоновом потоке, не блокируя скролл
                                            alt="attachment"
                                            onError={() => setImageErrors(prev => ({ ...prev, [msg.id]: true }))}
                                            onLoad={() => {
                                                // Если это последнее сообщение - пушим скролл вниз
                                                if (isLastMessage) scrollToBottom(); 
                                            }}
                                            style={{
                                                width: '100%', maxWidth: '300px', maxHeight: '400px',
                                                objectFit: 'cover', borderRadius: '12px',
                                                filter: msg.isUploading ? 'blur(4px) grayscale(50%)' : 'none'
                                            }}
                                        />
                                    )}
                                    {msg.isUploading && (
                                        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                                            <CircularProgress size={24} color="inherit" />
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {msg.text && (msg.text !== '📷 Фото' || !imageSrc) && (
                                <MessageTxt
                                    msg={msg}
                                    isMine={isMine}
                                    onReply={(message) => {
                                        setReplyTo(message);
                                        const input = document.querySelector('textarea, input[type="text"]');
                                        if (input instanceof HTMLElement) input.focus();
                                    }}
                                />
                            )}

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 0.5, px: 0.5 }}>
                                {isMine && msg.status === 'failed' && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 'auto', pr: 1, gap: 0.5, color: 'error.main' }}>
                                        <ErrorOutlineIcon sx={{ fontSize: 16 }} />
                                        <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                            Не отправлено
                                        </Typography>
                                        <Button
                                            size="small"
                                            color="error"
                                            onClick={() => onRetry?.(msg)}
                                            sx={{
                                                minWidth: 0,
                                                py: 0,
                                                px: 0.5,
                                                textTransform: 'none',
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                lineHeight: 1.4
                                            }}
                                        >
                                            Повторить
                                        </Button>
                                    </Box>
                                )}
                                {isMine && msg.status === 'queued' && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 'auto', pr: 1, gap: 0.5, color: 'text.secondary' }}>
                                        <AccessTimeIcon sx={{ fontSize: 16 }} />
                                        <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                            Отправится при появлении связи
                                        </Typography>
                                    </Box>
                                )}
                                <Typography variant="caption" sx={{ opacity: 0.6, mr: 0.5, fontSize: '0.75rem' }}>
                                    {new Date(msg.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </Typography>
                                {isMine && msg.status !== 'failed' && msg.status !== 'queued' && <OutgoingStatus message={msg} />}
                            </Box>
                        </Paper>
                    </Box>
                );
            })}

            {/* 3. Невидимый часовой для загрузки старых сообщений. 
                Из-за flexDirection: 'column-reverse' этот блок будет визуально НАВЕРХУ чата.
            */}
            {hasMore && (
                <Box ref={ref} sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                </Box>
            )}
        </Box>
    );

};
